import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const TRANSITION_DURATION = 360;
let selectionVersion = 0;
let activeTransition;

function getVisibleImage(scroller) {
  if (!scroller) return;
  const left = scroller.getBoundingClientRect().left;
  let nearestSlide;
  let nearestDistance = Infinity;

  for (const slide of scroller.querySelectorAll('slideshow-slide')) {
    const distance = Math.abs(slide.getBoundingClientRect().left - left);
    if (distance < nearestDistance) {
      nearestSlide = slide;
      nearestDistance = distance;
    }
  }

  return nearestSlide?.querySelector('img');
}

function makePanel(source, imageUrl) {
  const panel = document.createElement('div');
  const image = document.createElement('img');
  const style = getComputedStyle(source);

  panel.style.cssText = 'position:absolute;inset:0;will-change:transform;';
  image.alt = '';
  image.decoding = 'async';
  image.loading = 'eager';
  image.style.cssText = 'display:block;width:100%;height:100%;';
  image.style.objectFit = style.objectFit;
  image.style.objectPosition = style.objectPosition;
  image.src = imageUrl;
  panel.append(image);

  return { panel, image };
}

export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const version = ++selectionVersion;
    const previousVisual = activeTransition?.previewImage?.naturalWidth
      ? activeTransition.previewImage
      : undefined;
    activeTransition?.cancel();

    const optionId = String(event.detail?.optionValueId || '');
    const option = Array.from(event.target.querySelectorAll('[data-option-value-id]')).find(
      (element) => element.dataset.optionValueId === optionId,
    );
    const previousOption = option?.closest('fieldset')?.querySelector('input[data-previous-checked="true"]');
    const direction = Number(option?.dataset.inputIndex) < Number(previousOption?.dataset.inputIndex) ? -1 : 1;
    const transition = this.#startImmediateSlide(option, version, direction, previousVisual);

    event.promise
      .then(({ detail }) => {
        if (version !== selectionVersion || !this.isConnected || !detail?.html) return;
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        if (transition) transition.replace(newMediaGallery);
        else this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        transition?.cancel();
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };

  #startImmediateSlide(option, version, direction, previousVisual) {
    const previewUrl = option?.dataset.featuredMediaUrl;
    const targetMediaId = option?.dataset.featuredMediaId;
    const oldScroller = this.querySelector('slideshow-slides');
    const oldImage = getVisibleImage(oldScroller);
    const visualSource = previousVisual || oldImage;
    const bounds = oldScroller?.getBoundingClientRect();

    if (
      !previewUrl ||
      !oldImage ||
      !visualSource?.naturalWidth ||
      !bounds?.width ||
      !bounds.height ||
      bounds.bottom <= 0 ||
      bounds.top >= window.innerHeight ||
      this.dataset.presentation !== 'carousel' ||
      matchMedia('(prefers-reduced-motion: reduce)').matches ||
      (targetMediaId && this.querySelector(`[data-media-id="${targetMediaId}"]`))
    ) {
      return;
    }

    const oldPanel = makePanel(visualSource, visualSource.currentSrc || visualSource.src);
    const nextPanel = makePanel(oldImage, previewUrl);
    nextPanel.image.fetchPriority = 'high';
    const overlay = document.createElement('div');
    overlay.dataset.variantMediaTransition = '';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText =
      'position:fixed;overflow:hidden;pointer-events:none;z-index:100;isolation:isolate;';
    const background = getComputedStyle(document.body).backgroundColor;
    overlay.style.background = background === 'rgba(0, 0, 0, 0)' ? '#fff' : background;
    overlay.append(oldPanel.panel, nextPanel.panel);

    let cancelled = false;
    let replacement;
    let newScroller;
    let fullReady = false;
    let previewReady = false;
    let animationDone = false;
    let oldAnimation;
    let newAnimation;
    let holdTimeout;

    const positionOverlay = () => {
      const scroller = newScroller || oldScroller;
      const rect = scroller.getBoundingClientRect();
      overlay.style.left = `${rect.left}px`;
      overlay.style.top = `${rect.top}px`;
      overlay.style.width = `${rect.width}px`;
      overlay.style.height = `${rect.height}px`;
      overlay.style.borderRadius = getComputedStyle(scroller).borderRadius;
    };
    const cancel = () => {
      if (cancelled) return;
      cancelled = true;
      clearTimeout(holdTimeout);
      oldAnimation?.cancel();
      newAnimation?.cancel();
      window.removeEventListener('scroll', positionOverlay, true);
      window.removeEventListener('resize', positionOverlay);
      overlay.remove();
      if (activeTransition?.cancel === cancel) activeTransition = undefined;
    };
    const finishWhenReady = () => {
      if (replacement && fullReady && (!previewReady || animationDone)) cancel();
    };

    activeTransition = { cancel, previewImage: nextPanel.image };
    positionOverlay();
    document.body.append(overlay);
    window.addEventListener('scroll', positionOverlay, true);
    window.addEventListener('resize', positionOverlay);

    nextPanel.image.decode().then(() => {
      if (cancelled || version !== selectionVersion) return;
      previewReady = true;
      positionOverlay();
      const options = {
        duration: TRANSITION_DURATION,
        easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
        fill: 'forwards',
      };
      const incomingOffset = direction > 0 ? '100%' : '-100%';
      const outgoingOffset = direction > 0 ? '-100%' : '100%';
      oldAnimation = oldPanel.panel.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${outgoingOffset})` }],
        options,
      );
      newAnimation = nextPanel.panel.animate(
        [{ transform: `translateX(${incomingOffset})` }, { transform: 'translateX(0)' }],
        options,
      );
      Promise.allSettled([oldAnimation.finished, newAnimation.finished]).then(() => {
        animationDone = true;
        finishWhenReady();
      });
    }).catch(() => {
      finishWhenReady();
    });

    return {
      cancel,
      replace: (newMediaGallery) => {
        if (cancelled) return;
        this.replaceWith(newMediaGallery);
        replacement = newMediaGallery;
        newScroller = newMediaGallery.querySelector('slideshow-slides');
        if (!newScroller) return cancel();
        positionOverlay();

        const fullImage = newMediaGallery.querySelector('slideshow-slide[aria-hidden="false"] img');
        if (!fullImage || typeof fullImage.decode !== 'function') return cancel();
        holdTimeout = setTimeout(() => {
          fullReady = true;
          finishWhenReady();
        }, 3000);
        fullImage.decode().catch(() => {}).then(() => {
          if (cancelled) return;
          fullReady = true;
          finishWhenReady();
        });
      },
    };
  }

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (zoomDialogMedia) this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
